// src/hooks/useWatchlist.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { watchlistService } from '../services/watchlistService';

export const useWatchlist = () => {
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initializedRef = useRef(false);

  const fetchWatchlist = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await watchlistService.getWatchlist();
      setWatchlist(data || []);
    } catch (err) {
      setError(err.message);
      console.error('useWatchlist fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Prevent duplicate fetch in React StrictMode
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      fetchWatchlist();
    }
  }, [fetchWatchlist]);

  // Listen for watchlist changes dispatched by StockDetail eye toggle
  // so the dashboard section stays in sync without a full refetch on every render
  useEffect(() => {
    const handleWatchlistChange = () => {
      fetchWatchlist();
    };
    window.addEventListener('watchlistChange', handleWatchlistChange);
    return () => window.removeEventListener('watchlistChange', handleWatchlistChange);
  }, [fetchWatchlist]);

  const addToWatchlist = useCallback(async (symbol) => {
    try {
      await watchlistService.addToWatchlist(symbol);
      // Optimistic: trigger global event so any mounted useWatchlist refetches
      window.dispatchEvent(new CustomEvent('watchlistChange'));
      return true;
    } catch (err) {
      console.error('addToWatchlist error:', err);
      return false;
    }
  }, []);

  const removeFromWatchlist = useCallback(async (symbol) => {
    try {
      await watchlistService.removeFromWatchlist(symbol);
      // Optimistic local removal
      setWatchlist(prev => prev.filter(item => item.stock.symbol !== symbol));
      // Also notify other instances
      window.dispatchEvent(new CustomEvent('watchlistChange'));
      return true;
    } catch (err) {
      console.error('removeFromWatchlist error:', err);
      // Re-sync from server on error
      await fetchWatchlist();
      return false;
    }
  }, [fetchWatchlist]);

  return {
    watchlist,
    loading,
    error,
    refetch: fetchWatchlist,
    addToWatchlist,
    removeFromWatchlist,
  };
};